import { useId, type Ref } from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

type LabeledSelectOption<TValue extends string> = {
  value: TValue
  label: string
}

type LabeledSelectProps<TValue extends string> = {
  value: TValue
  onValueChange: (value: TValue) => void
  options: readonly LabeledSelectOption<TValue>[]
  label?: string
  triggerId?: string
  triggerRef?: Ref<HTMLButtonElement>
  wrapperClassName?: string
  triggerClassName?: string
  className?: string
  placeholder?: string
  disabled?: boolean
}

const getLabeledSelectDisplayValue = <TValue extends string>(
  value: TValue | null | undefined,
  options: readonly LabeledSelectOption<TValue>[],
  placeholder?: string,
) => {
  if (value == null || value === "") {
    return placeholder
  }

  const match = options.find((option) => option.value === value)
  return match?.label ?? String(value)
}

function LabeledSelect<TValue extends string>({
  value,
  onValueChange,
  options,
  label,
  triggerId,
  triggerRef,
  wrapperClassName,
  triggerClassName,
  className,
  placeholder,
  disabled,
}: LabeledSelectProps<TValue>) {
  const generatedId = useId()
  const resolvedTriggerId = triggerId ?? generatedId
  const select = (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(nextValue) => onValueChange(nextValue as TValue)}
    >
      <SelectTrigger
        id={resolvedTriggerId}
        ref={triggerRef}
        className={cn(label ? "w-full" : undefined, className, triggerClassName)}
      >
        <SelectValue placeholder={placeholder}>
          {(selectedValue) =>
            getLabeledSelectDisplayValue(
              selectedValue as TValue | null | undefined,
              options,
              placeholder,
            )
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="start">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  if (!label) {
    return select
  }

  return (
    <div className={cn("grid gap-1.5", wrapperClassName)}>
      <Label htmlFor={resolvedTriggerId}>{label}</Label>
      {select}
    </div>
  )
}

export {
  getLabeledSelectDisplayValue,
  LabeledSelect,
  type LabeledSelectOption,
}
